import { bilanDeLaSemaine, semaineOrganisee, tourneeDuChapitre } from '@neopolis/engine';
import type { Content, GameState } from '@neopolis/engine';

import { T } from '../../textes-interface.js';

/**
 * Étape 3 — Le bilan de la semaine.
 *
 * Trois grandeurs seulement : kilomètres, carburant, CO₂. Les débordements n'y
 * figurent pas comme un quatrième chiffre — ils arrivent après, comme une
 * conséquence, parce que c'est ainsi qu'un service se juge : on ne félicite pas
 * une collecte qui n'a rien collecté.
 */
export function EtapeBilanTournees({ content, partie }: { content: Content; partie: GameState }) {
  const tournee = tourneeDuChapitre(content, partie.chapterId);
  if (!tournee) return null;

  if (!semaineOrganisee(partie, content)) {
    return <p className="font-semibold text-slate-700">{T.bilanCollecte.aucuneTournee}</p>;
  }

  const bilan = bilanDeLaSemaine(partie, content);
  const economie = bilan.actuelle.km - bilan.km;
  const economieCo2 = bilan.actuelle.kgCo2 - bilan.kgCo2;

  return (
    <section className="space-y-4">
      <h2 className="text-xl font-bold text-slate-900">{T.bilanCollecte.titre}</h2>

      <div className="overflow-x-auto rounded-2xl border-2 border-slate-200 bg-white">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b-2 border-slate-300">
              <th className="p-3">{T.bilanCollecte.colonneJour}</th>
              <th className="p-3">{T.bilanCollecte.colonneArrets}</th>
              <th className="p-3 text-right">{T.bilanCollecte.colonneKm}</th>
              <th className="p-3 text-right">{T.bilanCollecte.colonneCarburant}</th>
              <th className="p-3 text-right">{T.bilanCollecte.colonneCo2}</th>
            </tr>
          </thead>
          <tbody>
            {bilan.jours.map((jour) => (
              <tr key={jour.jour} className="border-b border-slate-200">
                <td className="p-3 font-semibold capitalize">{jour.jour}</td>
                <td className="p-3 tabular-nums">
                  {jour.conteneurs.filter((c) => c.collecte).length}
                </td>
                <td className="p-3 text-right tabular-nums">
                  {Math.round(jour.cout.km)} {T.bilanCollecte.km}
                </td>
                <td className="p-3 text-right tabular-nums">
                  {Math.round(jour.cout.litres)} {T.bilanCollecte.litres}
                </td>
                <td className="p-3 text-right tabular-nums">
                  {Math.round(jour.cout.kgCo2)} {T.bilanCollecte.kgCo2}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <dl className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-2xl bg-slate-100 p-4">
          <dt className="text-sm text-slate-600">{T.bilanCollecte.totalActuelle}</dt>
          <dd className="text-2xl font-bold tabular-nums text-slate-900">
            {Math.round(bilan.actuelle.km)} {T.bilanCollecte.km}
          </dd>
          <dd className="text-sm text-slate-600">
            {Math.round(bilan.actuelle.kgCo2)} {T.bilanCollecte.kgCo2} {T.bilanCollecte.colonneCo2}
          </dd>
        </div>
        <div className="rounded-2xl bg-sky-50 p-4">
          <dt className="text-sm text-slate-600">{T.bilanCollecte.totalVotre}</dt>
          <dd className="text-2xl font-bold tabular-nums text-sky-900">
            {Math.round(bilan.km)} {T.bilanCollecte.km}
          </dd>
          <dd className="text-sm text-slate-600">
            {Math.round(bilan.kgCo2)} {T.bilanCollecte.kgCo2} {T.bilanCollecte.colonneCo2}
          </dd>
        </div>
        <div className="rounded-2xl bg-emerald-50 p-4">
          <dt className="text-sm text-slate-600">{T.bilanCollecte.economie}</dt>
          <dd className="text-2xl font-bold tabular-nums text-emerald-800">
            {Math.round(economie)} {T.bilanCollecte.km}
          </dd>
          <dd className="text-sm text-slate-600">
            {Math.round(economieCo2)} {T.bilanCollecte.kgCo2} {T.bilanCollecte.colonneCo2}
          </dd>
        </div>
      </dl>

      {bilan.debordements > 0 ? (
        <p className="rounded-2xl bg-rose-50 p-4 font-semibold text-rose-800">
          {T.bilanCollecte.debordements} : {bilan.debordements}
        </p>
      ) : (
        <p className="rounded-2xl bg-emerald-50 p-4 font-semibold text-emerald-800">
          {T.bilanCollecte.aucunDebordement}
        </p>
      )}

      <p className="text-slate-800">{bilan.message}</p>
    </section>
  );
}
